import React, { useMemo } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import {
  configApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import useAsync from 'react-use/esm/useAsync';
import { ScaffolderField } from '@backstage/plugin-scaffolder-react/alpha';
import {
  CircularProgress,
  FormControl,
  FormHelperText,
  TextField,
  makeStyles,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import Alert from '@material-ui/lab/Alert';

interface TeamEntity {
  metadata: {
    name: string;
    annotations?: Record<string, string>;
  };
}

interface TeamOption {
  name: string;
  permitted: boolean;
}

const useStyles = makeStyles(() => ({
  permittedOption: {
    fontWeight: 600,
  },
  nonPermittedOption: {
    opacity: 0.5,
    fontStyle: 'italic',
  },
}));

export const CatalogTeamPicker = ({
  onChange,
  errors,
  rawErrors,
  required,
  help,
  disabled,
  schema: { title = 'Repository Teams', description, type: schemaType },
  formData,
  uiSchema,
  formContext,
}: FieldExtensionComponentProps<string | string[]>) => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const fetchApi = useApi(fetchApiRef);

  const uiOptions = (uiSchema?.['ui:options'] as Record<string, unknown>) ?? {};
  const systemField = (uiOptions.systemField as string) ?? 'ref';
  const permissionFilter = uiOptions.permissionFilter as string | undefined;
  const permissionField = uiOptions.permissionField as string | undefined;
  const multiple = schemaType === 'array';

  const systemRef: string = (formContext as any)?.formData?.[systemField] ?? '';
  // Dynamic field value takes precedence over static permissionFilter
  const permissionRef: string = permissionField
    ? ((formContext as any)?.formData?.[permissionField] ?? '')
    : (permissionFilter ?? '');

  const backendUrl = config.getString('backend.baseUrl');

  const { value, loading, error } = useAsync(async (): Promise<TeamOption[]> => {
    if (!systemRef) return [];

    const params = new URLSearchParams();
    params.append('filter', 'spec.type=GitHub Team');
    params.set('fields', 'metadata.name,metadata.annotations');
    params.set('limit', '1000');

    const res = await fetchApi.fetch(
      `${backendUrl}/api/catalog/entities/by-query?${params.toString()}`,
      {
        method: 'GET',
        headers: { Accept: 'application/json' },
      },
    );

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Failed to fetch teams: ${res.status} ${text}`);
    }

    const data = await res.json();
    const allTeams: TeamEntity[] = data.items ?? [];

    return allTeams
      .filter(t => t.metadata.annotations?.['nestle.com/parent-system'] === systemRef)
      .map(t => ({
        name: t.metadata.name,
        permitted:
          !permissionRef ||
          t.metadata.annotations?.['nestle.com/github-team-permission'] === permissionRef,
      }))
      .sort((a, b) => {
        if (a.permitted === b.permitted) return a.name.localeCompare(b.name);
        return a.permitted ? -1 : 1;
      });
  }, [systemRef, permissionRef]); // eslint-disable-line react-hooks/exhaustive-deps

  const teamOptions = useMemo(() => value ?? [], [value]);

  const selectedTeams = useMemo(() => {
    if (!formData) return multiple ? [] : null;
    if (multiple) {
      const names = Array.isArray(formData) ? formData : [formData];
      return teamOptions.filter(o => names.includes(o.name));
    }
    return teamOptions.find(o => o.name === formData) ?? null;
  }, [formData, multiple, teamOptions]);

  if (!systemRef) {
    return (
      <ScaffolderField
        rawErrors={rawErrors}
        errors={errors}
        required={required}
        help={help}
        rawDescription={description}
        disabled={disabled}
      >
        <FormControl fullWidth disabled>
          <Autocomplete
            multiple={multiple}
            options={[]}
            disabled
            getOptionLabel={(o: TeamOption) => o.name}
            renderInput={params => (
              <TextField {...params} label={title} placeholder="Select a system first" />
            )}
          />
        </FormControl>
      </ScaffolderField>
    );
  }

  if (loading) {
    return (
      <ScaffolderField
        rawErrors={rawErrors}
        errors={errors}
        required={required}
        help={help}
        rawDescription={description}
        disabled={disabled}
      >
        <FormControl fullWidth>
          <Autocomplete
            multiple={multiple}
            options={[]}
            loading
            disabled
            getOptionLabel={(o: TeamOption) => o.name}
            renderInput={params => (
              <TextField
                {...params}
                label={title}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      <CircularProgress color="inherit" size={20} />
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </FormControl>
      </ScaffolderField>
    );
  }

  if (error) {
    return (
      <ScaffolderField
        rawErrors={[...(rawErrors || []), `Failed to load teams: ${error.message}`]}
        errors={errors}
        required={required}
        help={help}
        rawDescription={description}
        disabled={disabled}
      >
        <Alert severity="error">
          Failed to load teams: {error.message}
        </Alert>
      </ScaffolderField>
    );
  }

  return (
    <ScaffolderField
      rawErrors={rawErrors}
      errors={errors}
      required={required}
      help={help}
      rawDescription={description}
      disabled={disabled}
    >
      <FormControl fullWidth error={rawErrors && rawErrors.length > 0}>
        <Autocomplete
          multiple={multiple}
          options={teamOptions}
          value={selectedTeams}
          getOptionLabel={(o: TeamOption) => o.name}
          getOptionSelected={(o: TeamOption, v: TeamOption) => o.name === v.name}
          getOptionDisabled={(o: TeamOption) => !o.permitted}
          groupBy={(o: TeamOption) => (o.permitted ? 'Available teams' : 'Non-available teams')}
          onChange={(_, newValue) => {
            if (multiple) {
              onChange((newValue as TeamOption[]).map(o => o.name));
            } else {
              onChange((newValue as TeamOption | null)?.name ?? undefined);
            }
          }}
          disabled={disabled || teamOptions.length === 0}
          renderOption={(o: TeamOption) => (
            <span className={o.permitted ? classes.permittedOption : classes.nonPermittedOption}>
              {o.name}
            </span>
          )}
          renderInput={params => (
            <TextField
              {...params}
              label={title}
              placeholder="Search or select teams..."
            />
          )}
          noOptionsText="No teams found for this system"
        />
        {help && <FormHelperText>{help}</FormHelperText>}
      </FormControl>
    </ScaffolderField>
  );
};
